import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { FixedSizeGrid as Grid } from "react-window";

import { Skeleton } from "@mui/material";
import { PaginationLoading, PaginationNotFound } from "./paginationTools";
import * as S from "./styles";
import { PaginationCustomRequest, PaginationRequest } from "@/main/models/common/paginationRequest";
import { PaginationResponse } from "@/main/models/common/paginationResponse";
import { isNullUndefined } from "@/main/services/reducerUtils";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { getDefaultMessageResultsFound, getTranslateByKey } from "@/main/services/utils";

export type IFetchMoreRequest<T = any> = T & {
	pageIndex?: number;
};
export interface IFetchMoreResponse {
	data: any[];
	totalNumberOfRecords: number;
}
export type ITypeGrid = "boxGrid" | "default";

interface IKOGPaginationGridVirtualizedProps {
	dataGrid: any[];
	formFilters: PaginationRequest | null;
	fetchMore: (request: PaginationRequest | PaginationCustomRequest) => Promise<PaginationResponse<any>>;
	setDataGrid: React.Dispatch<React.SetStateAction<any[]>>;

	// Virtualization props
	itemWidth?: number;
	itemHeight?: number;
	gap?: number;
	renderItem: (item: any, index: number) => React.ReactNode;

	// Optional props
	typeGrid?: ITypeGrid;
	showTitleResults?: boolean;
	showEndMessage?: boolean;
	fetchEqualFilters?: boolean;
	totalRecords?: number;
	sumTotalRecords?: number;
	isLoading?: boolean;
	maxHeightScrollInternal?: string;
	messageResultNotFound?: string;
	setTotalRecords?: React.Dispatch<React.SetStateAction<number>>;
	setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Componente to handle with pagination using virtualization
 *
 * @param dataGrid - Data to be rendered
 * @param formFilters - Filters to be applied
 * @param fetchMore - Function to fetch more data
 * @param setDataGrid - Function to set data
 * @param itemWidth - Width of each item in the grid (default: 200)
 * @param itemHeight - Height of each item in the grid (default: 190)
 * @param gap - Gap between items (default: 16)
 * @param renderItem - Function to render each item
 * @param typeGrid - Type of grid
 * @param showTitleResults - Show title results
 * @param showEndMessage - Show message when all results where loaded
 * @param fetchEqualFilters - if false, fetchMore will be called only if formFilters is different from the previous one
 * @param totalRecords - Total records (optional)
 * @param setTotalRecords - Function to set total records (optional)
 * @param sumTotalRecords - Function to add total records (optional)
 * @param isLoading - Loading state (optional)
 * @param maxHeightScrollInternal - Max height of the grid (default: calc(100vh - 280px))
 * @param messageResultNotFound - Message to be shown when no results are found (optional)
 * @param setIsLoading - Function to set loading state (optional)
 *
 * @example
 * const [data, setData] = useState<any[]>([]);
 * const [formFilters, setFormFilters] = useState<PaginationRequest | null>(null);
 *
 * async function fetchData(request: PaginationRequest) {
 *  return anyAPI.get(request).then(response => response.data);
 * }
 *
 * function getData() {
 *  const request: PaginationRequest = {
 *    pageIndex: PaginationEnum.PageIndexIntial,
 *    pageSize: PaginationEnum.PageSize10,
 *   };
 *  setFormFilters(request);
 *  setData([]);
 * }
 *
 * useEffect(() => {
 *  getData();
 * }, []);
 *
 * <KOGPaginationGridVirtualized
 *   dataGrid={data}
 *   formFilters={formFilters}
 *   fetchMore={fetchData}
 *   setDataGrid={setData}
 *   renderItem={(item, index) => <CardComponent key={index} data={item} />}
 * />
 *
 */

function KOGPaginationGridVirtualized({
	typeGrid = "default",
	dataGrid,
	formFilters,
	itemWidth = 200,
	itemHeight = 190,
	gap = 16,
	renderItem,
	showTitleResults = false,
	showEndMessage = true,
	fetchEqualFilters = true,
	maxHeightScrollInternal = "calc(100vh - 280px)",
	totalRecords: totalRecordsProp,
	sumTotalRecords = 0,
	setTotalRecords: setTotalRecordsProp,
	isLoading: isLoadingProp,
	setIsLoading: setIsLoadingProp,
	messageResultNotFound,
	fetchMore,
	setDataGrid,
}: PropsWithChildren<IKOGPaginationGridVirtualizedProps>) {
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(0);
	const [containerWidth, setContainerWidth] = useState(1200);
	const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const gridRef = useRef<any>(null);
	const isLoadingMore = useRef(false);

	const localIsLoadingState = useState(false);
	const [isLoading, setIsLoading] =
		isNullUndefined(isLoadingProp) || isNullUndefined(setIsLoadingProp)
			? localIsLoadingState
			: [isLoadingProp, setIsLoadingProp];
	const localTotalRecordsState = useState(0);
	const [totalRecords, setTotalRecords] =
		isNullUndefined(totalRecordsProp) || isNullUndefined(setTotalRecordsProp)
			? localTotalRecordsState
			: [totalRecordsProp, setTotalRecordsProp];
	const dependecyUseEffect = fetchEqualFilters ? formFilters : JSON.stringify(formFilters);

	// Calculate columns based on container width
	const columnCount = Math.max(1, Math.floor(containerWidth / (itemWidth + gap)));

	// Update container width on resize
	useEffect(() => {
		if (!containerRef.current) return;

		// Set initial width
		const initialWidth = containerRef.current.offsetWidth;
		if (initialWidth > 0) {
			setContainerWidth(initialWidth);
		}

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const width = entry.contentRect.width;
				if (width > 0) {
					setContainerWidth(width);
				}
			}
		});

		resizeObserver.observe(containerRef.current);
		return () => resizeObserver.disconnect();
	}, [containerRef.current]);

	async function onLoadInfiniteScroll(isInitialLoad: boolean = false) {
		const nextIndex = isInitialLoad ? PaginationEnum.PageIndexIntial : pageIndex + 1;
		const mountRequest: PaginationRequest = {
			...formFilters,
			pageIndex: nextIndex,
		};
		setPageIndex(nextIndex);

		await fetchMore(mountRequest).then((dataFetch) => {
			const { results = [], totalNumberOfRecords = 0 } = dataFetch;
			const newData = isInitialLoad ? results : [...dataGrid, ...results];
			if (isInitialLoad) setTotalRecords(totalNumberOfRecords);
			setDataGrid(newData);
		});
	}

	useEffect(() => {
		if (isNullUndefined(formFilters)) return;
		setIsLoading(true);
		const hasChangedIndexFilters =
			isNullUndefined(formFilters.pageIndex) || formFilters.pageIndex === PaginationEnum.PageIndexIntial;

		if (hasChangedIndexFilters) {
			setPageIndex(0);
		}

		onLoadInfiniteScroll(hasChangedIndexFilters).finally(() => setIsLoading(false));
	}, [dependecyUseEffect]);

	useEffect(() => {
		setHasShowMore(totalRecords > dataGrid.length);
	}, [totalRecords, dataGrid]);

	// Calculate grid dimensions
	// Adicionar skeleton cards baseado no pageSize quando estiver carregando mais dados
	const skeletonCount = hasShowMore && isLoadingMoreData ? formFilters?.pageSize || 10 : 0;
	const itemCount = dataGrid.length + skeletonCount;
	// Adicionar uma linha extra quando hasShowMore para garantir scroll
	const extraRow = hasShowMore ? 2 : 0;
	const totalRowCount = Math.ceil(itemCount / columnCount) + extraRow;

	// Calculate grid height
	const gridHeight = maxHeightScrollInternal.includes("calc")
		? maxHeightScrollInternal
		: typeof maxHeightScrollInternal === "string"
			? maxHeightScrollInternal
			: `${maxHeightScrollInternal}px`;

	const gridHeightPx =
		typeof gridHeight === "string" && gridHeight.includes("calc")
			? Math.max(window.innerHeight - 280, 400)
			: Math.max(parseInt(gridHeight) || 600, 400);

	// Handle scroll to load more items
	const handleScroll = useCallback(
		({ scrollTop, scrollUpdateWasRequested }: any) => {
			// Ignore programmatic scrolls
			if (scrollUpdateWasRequested) return;

			if (!hasShowMore || isLoadingMore.current || isLoading) return;

			// Calculate total content height
			const totalContentHeight = totalRowCount * (itemHeight + gap);
			const scrollBottom = scrollTop + gridHeightPx;
			const threshold = totalContentHeight - gridHeightPx * 0.5;

			if (scrollBottom >= threshold) {
				isLoadingMore.current = true;
				setIsLoadingMoreData(true);
				const nextIndex = pageIndex + 1;
				const mountRequest: PaginationRequest = {
					...formFilters,
					pageIndex: nextIndex,
				};
				setPageIndex(nextIndex);

				fetchMore(mountRequest)
					.then((dataFetch) => {
						const { results = [] } = dataFetch;
						const newData = [...dataGrid, ...results];
						setDataGrid(newData);
					})
					.catch((error) => {
						console.error("Error loading more items:", error);
					})
					.finally(() => {
						isLoadingMore.current = false;
						setIsLoadingMoreData(false);
					});
			}
		},
		[
			hasShowMore,
			isLoading,
			gridHeightPx,
			pageIndex,
			formFilters,
			fetchMore,
			dataGrid,
			setDataGrid,
			totalRowCount,
			itemHeight,
			gap,
		],
	);

	// Cell renderer for react-window
	const Cell = useCallback(
		({ columnIndex, rowIndex, style }: any) => {
			const index = rowIndex * columnCount + columnIndex;

			if (index >= dataGrid.length) {
				// Renderizar skeleton baseado no pageSize quando estiver carregando
				const skeletonCount = formFilters?.pageSize || 10;
				if (hasShowMore && isLoadingMoreData && index < dataGrid.length + skeletonCount) {
					return (
						<div
							style={{
								...style,
								left: Number(style.left) + gap,
								top: Number(style.top) + gap,
								width: Number(style.width) - gap,
								height: Number(style.height) - gap,
							}}
						>
							<S.AreaSkeleton>
								<Skeleton variant="rectangular" width="182px" height="140px" />
								<Skeleton variant="text" width="140px" height="20px" style={{ marginTop: "8px" }} />
							</S.AreaSkeleton>
						</div>
					);
				}
				return null;
			}

			const item = dataGrid[index];

			return (
				<div
					style={{
						...style,
						left: Number(style.left) + gap,
						top: Number(style.top) + gap,
						width: Number(style.width) - gap,
						height: Number(style.height) - gap,
					}}
				>
					{renderItem(item, index)}
				</div>
			);
		},
		[dataGrid, columnCount, gap, renderItem, hasShowMore, isLoadingMoreData, typeGrid, containerWidth, itemWidth],
	);

	const RenderLoading = () => <PaginationLoading typeGrid={typeGrid} />;

	if (dataGrid.length === 0 && formFilters === null) return null;
	if (isLoading && dataGrid.length === 0) return <RenderLoading />;
	if (formFilters !== null && dataGrid.length === 0 && !isLoading)
		return <PaginationNotFound typeGrid={typeGrid} messageResultNotFound={messageResultNotFound} />;

	if (dataGrid.length === 0) return null;

	const titleBoxGrid = getDefaultMessageResultsFound(dataGrid.length, sumTotalRecords + totalRecords);

	return (
		<div style={{ width: "100%" }}>
			<style>
				{`
					@keyframes pulse {
						0%, 100% {
							opacity: 1;
						}
						50% {
							opacity: 0.5;
						}
					}
				`}
			</style>
			{showTitleResults && <S.TitleResults>{titleBoxGrid}</S.TitleResults>}

			<div ref={containerRef} style={{ width: "100%" }}>
				<Grid
					ref={gridRef}
					columnCount={columnCount}
					columnWidth={itemWidth + gap}
					height={gridHeightPx}
					rowCount={totalRowCount}
					rowHeight={itemHeight + gap}
					width={containerWidth}
					overscanRowCount={100}
					onScroll={handleScroll}
				>
					{Cell}
				</Grid>
			</div>

			{!hasShowMore && dataGrid.length > 0 && showEndMessage && (
				<S.EndMessage className="d-flex d-flex justify-content-center">
					{getTranslateByKey("information.allResultsWereDisplayed")}
				</S.EndMessage>
			)}
		</div>
	);
}

export default KOGPaginationGridVirtualized;
